/**
 * sessionBootstrap
 *
 * On page load the Redux store starts empty (no token in localStorage).
 * This module calls the session-exchange endpoint — which reads the
 * httpOnly JWT cookie set at sign-in — to get a fresh token and user,
 * then dispatches them into the store so the app renders authenticated.
 *
 * The old token-in-localStorage flow has been removed as part of the
 * httpOnly cookie migration (security fix #1).
 */

const SESSION_EXCHANGE_URL = '/api/v1/auth/session/exchange';

const invalidAuthCodes = new Set([
  'E_FORCED_LOGOUT',
  'E_UNAUTHORIZED',
  'AUTH_REQUIRED',
  'invalid_token',
]);

export const clearStoredAuth = () => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem('token');
    localStorage.removeItem('firstLogin');
    localStorage.removeItem('user');
  } catch (e) {
    // Ignore storage cleanup failures.
  }
};

const parseResponseBody = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
};

export const bootstrapLegacySession = async ({ store, skip = false } = {}) => {
  if (skip) return { status: 'skipped', restored: false, clearAuth: false };

  try {
    const response = await fetch(SESSION_EXCHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      const body = await parseResponseBody(response);
      const code = body?.code || body?.error || body?.message;
      if (invalidAuthCodes.has(code) || response.status === 401) {
        clearStoredAuth();
        return { status: 'invalid-session', restored: false, clearAuth: true };
      }
      return { status: 'failed', restored: false, clearAuth: false };
    }

    const body = await parseResponseBody(response);
    if (body && body.token && body.user) {
      if (store && typeof store.dispatch === 'function') {
        store.dispatch({ type: 'SESSION_RESTORED', payload: body });
      }
      return { status: 'restored', restored: true, clearAuth: false };
    }

    return { status: 'empty', restored: false, clearAuth: false };
  } catch (error) {
    return { status: 'error', restored: false, clearAuth: false, error };
  }
};
